import {
  ColumnWrapStack,
  GeneralErrorUI,
  LoadingProgress,
  useDataGridContext,
  ViewSection,
} from '@issuemanager/apps/client/components';
import { useUserContext } from '@issuemanager/apps/client/contexts';
import { useQuickFilterStatistics } from '@issuemanager/client/features/issues';
import {
  ISSUE_FIELD_APPROVER,
  ISSUE_FIELD_CREATOR,
  ISSUE_FIELD_DUE_DATE,
  ISSUE_FIELD_ISSUE_STATUS,
  ISSUE_FIELD_ISSUE_TYPE,
  ISSUE_FIELD_OWNER,
  ISSUE_FIELD_SLA_DUE_DAYS,
  IssueColumnVisibilityName,
} from '@issuemanager/constants';
import {
  CreatedIssuesEnum,
  ManagedIssuesEnum,
  OwnedActionsEnum,
  OwnedIssuesEnum,
  QuickFilterAllEnum,
  ToBeApprovedEnum,
} from '@issuemanager/test/constants';
import { Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CreatedIssues } from './created-issues';
import { ManagedIssues } from './managed-issues';
import { OwnedActions } from './owned-actions';
import { OwnedIssues } from './owned-issues';
import { ToBeApproved } from './to-be-approved';

const quickFilterMap: Record<IssueColumnVisibilityName, QuickFilterAllEnum[]> =
  {
    [ISSUE_FIELD_CREATOR]: [
      CreatedIssuesEnum.ALL,
      CreatedIssuesEnum.DRAFT,
      CreatedIssuesEnum.IN_REVIEW,
      CreatedIssuesEnum.IN_PROGRESS,
      CreatedIssuesEnum.REMEDIATION_PLANNING,
      CreatedIssuesEnum.IN_REMEDIATION,
      CreatedIssuesEnum.TO_BE_PUBLISHED,
      CreatedIssuesEnum.PENDING_ACCEPTANCE,
    ],
    [ISSUE_FIELD_ISSUE_STATUS]: [
      CreatedIssuesEnum.DRAFT,
      CreatedIssuesEnum.IN_REVIEW,
      CreatedIssuesEnum.IN_PROGRESS,
      CreatedIssuesEnum.REMEDIATION_PLANNING,
      CreatedIssuesEnum.IN_REMEDIATION,
      CreatedIssuesEnum.TO_BE_PUBLISHED,
      CreatedIssuesEnum.PENDING_ACCEPTANCE,
      ManagedIssuesEnum.IN_REVIEW,
      ManagedIssuesEnum.OVERDUE,
      ManagedIssuesEnum.SLA_OVERDUE,
      OwnedIssuesEnum.OVERDUE,
      OwnedIssuesEnum.TO_BE_APPROVED,
      OwnedActionsEnum.OPEN,
      OwnedActionsEnum.IN_PROGRESS,
      OwnedActionsEnum.TO_BE_APPROVED,
      ToBeApprovedEnum.ISSUES_TO_APPROVE,
      ToBeApprovedEnum.ACTIONS_TO_APPROVE,
    ],
    [ISSUE_FIELD_ISSUE_TYPE]: [
      ManagedIssuesEnum.ALL,
      ManagedIssuesEnum.IN_REVIEW,
      ManagedIssuesEnum.OVERDUE,
      ManagedIssuesEnum.SLA_OVERDUE,
    ],
    [ISSUE_FIELD_DUE_DATE]: [
      ManagedIssuesEnum.OVERDUE,
      OwnedIssuesEnum.OVERDUE,
      OwnedActionsEnum.OVERDUE,
    ],
    [ISSUE_FIELD_OWNER]: [
      OwnedIssuesEnum.ALL,
      OwnedIssuesEnum.OVERDUE,
      OwnedIssuesEnum.TO_BE_APPROVED,
      OwnedActionsEnum.ALL,
      OwnedActionsEnum.IN_PROGRESS,
      OwnedActionsEnum.OVERDUE,
      OwnedActionsEnum.OPEN,
      OwnedActionsEnum.TO_BE_APPROVED,
    ],
    [ISSUE_FIELD_APPROVER]: [
      ToBeApprovedEnum.ISSUES_TO_APPROVE,
      ToBeApprovedEnum.ACTIONS_TO_APPROVE,
      OwnedIssuesEnum.TO_BE_APPROVED,
      OwnedActionsEnum.TO_BE_APPROVED,
    ],
    [ISSUE_FIELD_SLA_DUE_DAYS]: [ManagedIssuesEnum.SLA_OVERDUE],
  };

export function QuickFilters() {
  const { userRoles } = useUserContext();
  const statistics = useQuickFilterStatistics();
  const { filterModel, columnVisibilityModel } = useDataGridContext();
  const [activeFilter, setActiveFilter] = useState<QuickFilterAllEnum | null>(
    null,
  );

  useEffect(() => {
    if (filterModel.source === 'grid') {
      setActiveFilter(null);
    }
  }, [filterModel.source]);

  useEffect(() => {
    if (!activeFilter) return;

    const isFilterHidden = Object.entries(quickFilterMap).some(
      ([field, filters]) =>
        filters.includes(activeFilter) &&
        columnVisibilityModel[field] === false,
    );

    if (isFilterHidden) {
      setActiveFilter(null);
    }
  }, [activeFilter, columnVisibilityModel]);

  const quickFilterItems = useMemo(
    () => [
      ...(userRoles?.hasCreatedIssues
        ? [
            <CreatedIssues
              key='created-issues'
              createdIssues={statistics.data?.quickFilterNumbers.createdIssues}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
            />,
          ]
        : []),
      ...(userRoles?.hasManagedIssueTypes
        ? [
            <ManagedIssues
              key='managed-issues'
              managedIssues={statistics.data?.quickFilterNumbers.managedIssues}
              slaDueDays={statistics.data?.columnFilters.overdueSLAs}
              issueTypesManagedByMe={
                statistics.data?.columnFilters.issueTypesManagedByMe
              }
              issueSubtypesManagedByMe={
                statistics.data?.columnFilters.issueSubtypesManagedByMe
              }
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
            />,
          ]
        : []),
      ...(userRoles?.hasOwnedIssues
        ? [
            <OwnedIssues
              key='owned-issues'
              ownedIssues={statistics.data?.quickFilterNumbers.ownedIssues}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
            />,
          ]
        : []),
      ...(userRoles?.hasOwnedActions
        ? [
            <OwnedActions
              key='owned-actions'
              ownedActions={statistics.data?.quickFilterNumbers.ownedActions}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
            />,
          ]
        : []),
      ...(userRoles?.hasApprovableIssues || userRoles?.hasApprovableActions
        ? [
            <ToBeApproved
              key='to-be-approved'
              toBeApproved={statistics.data?.quickFilterNumbers.toBeApproved}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
            />,
          ]
        : []),
    ],
    [
      userRoles?.hasCreatedIssues,
      userRoles?.hasManagedIssueTypes,
      userRoles?.hasOwnedIssues,
      userRoles?.hasOwnedActions,
      userRoles?.hasApprovableIssues,
      userRoles?.hasApprovableActions,
      statistics.dataUpdatedAt,
      activeFilter,
    ],
  );

  const { isAdmin: _isAdmin, ...rest } = userRoles || {};
  if (!Object.values(rest).some(Boolean)) {
    return null;
  }

  if (statistics.isError) {
    return <GeneralErrorUI />;
  }

  return (
    <Stack>
      <ViewSection padding='0'>
        {statistics.isFetching ? (
          <LoadingProgress height={250} />
        ) : (
          <ColumnWrapStack>{quickFilterItems}</ColumnWrapStack>
        )}
      </ViewSection>
    </Stack>
  );
}
